import { useLocation } from 'react-router-dom'

import { Box, Button, Divider, Text } from '@factorialco/deprecated-design-system'

import Portal from 'modules/core/components/Portal'
import TextField from 'modules/core/components/TextField'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import Viewer from 'modules/documents/components/FileViewer'
import VideoPlayer from 'modules/trainings/components/VideoPlayer'
import { trainingEvents } from 'modules/trainings/lib/events'

import * as styles from './index.vanilla.css'

export interface File {
  id: number
  contentType: string
  url: string
  filename: string
  title?: string | null
  navigation?: string
  type: 'training' | 'trainingClass' | 'membership'
}

interface Props<T extends File> {
  urlBackTo: string
  goNextFile: (file: T) => string
  goPrevFile: (file: T) => string
  files: T[]
  file?: T
}

export function FileViewer<T extends File>({
  urlBackTo,
  goNextFile,
  goPrevFile,
  files,
  file,
}: Props<T>) {
  const location = useLocation()
  const { navigation } = useNavigation()

  if (!file) {
    throw new Error(`File not found`)
  }

  return (
    <Portal>
      <div className={styles.overlay}>
        <Box flexDirection="row" style={{ flex: '1 1 auto' }}>
          <Box
            flexGrow
            alignItems="center"
            justifyContent="center"
            style={{ minHeight: '0', maxWidth: 'calc(100vw - 432px)' }}
          >
            {file?.contentType === 'video/mp4' ? (
              <Box
                padding="s16"
                width="full"
                style={{
                  aspectRatio: '16 / 9',
                  margin: 'auto',
                }}
              >
                <VideoPlayer
                  src={file.url}
                  onPercentageWatchedMilestone={(percentage_watched, video) => {
                    track(trainingEvents.videoTracking.progress, {
                      context: context(),
                      duration: Math.round(video.duration),
                      percentage_watched,
                    })

                    function context() {
                      return location.pathname.includes(`/${navigation.myTrainings.path}/`)
                        ? 'my_training'
                        : location.pathname.includes(
                            `/${navigation.trainings.path}/${navigation.trainings.catalog.path}/`
                          )
                        ? 'catalog'
                        : location.pathname.includes(
                            `/${navigation.trainings.path}/${navigation.trainings.list.path}/`
                          )
                        ? 'training'
                        : 'training' // default
                    }
                  }}
                />
              </Box>
            ) : (
              <Viewer
                mimeType={file?.contentType}
                url={file.url}
                filename={file.filename}
                isUrlAbsolute
              />
            )}
          </Box>
          <Box
            overflowY="auto"
            flexNoShrink
            style={{
              width: '432px',
              height: '100vh',
            }}
            border={{
              left: {
                width: 's1',
                style: 'solid',
                color: 'grey400',
              },
            }}
            gap="s12"
          >
            <Box gap="s1">
              <Box padding="s12" flexDirection="row" gap="s6" justifyContent="spaceBetween">
                <FilesNavigator
                  file={file}
                  files={files}
                  goNextFile={goNextFile}
                  goPrevFile={goPrevFile}
                />
                <Box gap="s1">
                  <Button.Router type="secondary" to={urlBackTo} iconName="cross" />
                </Box>
              </Box>
              <Divider />
            </Box>
            <Box padding="s12">
              <TextField
                value={file.filename}
                readonly
                label={i18n.t('trainings.training_files.filename')}
              />
            </Box>
          </Box>
        </Box>
      </div>
    </Portal>
  )
}

interface FilesNavigatorProps<T extends File> {
  file: T
  files: T[]
  goNextFile: (file: T) => string
  goPrevFile: (file: T) => string
}

function FilesNavigator<T extends File>({
  file,
  files,
  goNextFile,
  goPrevFile,
}: FilesNavigatorProps<T>) {
  const currentIndex = files.indexOf(file)
  const nextTrainingFile = files[currentIndex + 1]
  const previousTrainingFile = files[currentIndex - 1]

  return (
    <Box
      flexDirection="row"
      gap="s8"
      alignItems="center"
      justifyContent="spaceBetween"
      width="s360"
    >
      <Box width="fit-content" minWidth="s32">
        {currentIndex > 0 && previousTrainingFile && (
          <Button.Router
            to={goPrevFile(previousTrainingFile)}
            type="secondary"
            iconName="arrow-left"
          />
        )}
      </Box>
      <Box width="s240" flexDirection="row" justifyContent="center">
        <Text.Normal weight="medium" ellipsis>
          {file.title ?? file.filename}
        </Text.Normal>{' '}
        <Text.Normal weight="medium">{`(${currentIndex + 1}/${files.length})`}</Text.Normal>
      </Box>
      <Box width="fit-content" minWidth="s32">
        {nextTrainingFile && (
          <Button.Router
            to={goNextFile(nextTrainingFile)}
            type="secondary"
            iconName="arrow-right"
          />
        )}
      </Box>
    </Box>
  )
}
